import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

import { CopyButton } from '@/_shared/components/CopyButton';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { useSyntaxStyle } from '@/_shared/hooks/useSyntaxStyle';
import { codeBlockCustomStyle, SyntaxHighlighter } from '@/_shared/lib/syntax';

import { RudderstackSignInButton } from './PermissionNotice';
import { OutputSection } from './shared/OutputSection';

const date = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * Decision 1: Snowflake user, fingerprint, created, last rotated — for a managed-key Snowflake
 * connection only.
 *
 * The `source === 'workspace'` variant is CONVERSION SURFACE, and it is gated as such — on the
 * server, in `getKeyInfo`, which answers null for a legacy row when the legacy conversion is off for
 * the workspace. Nothing is re-derived here: this component gates on the payload's presence, so the
 * card can never show a "Workspace key" line for a conversion the server would refuse.
 */
export function KeyInfoLine({ connectionId }: { connectionId: string }): React.JSX.Element | null {
  const orpc = useOrpc();
  const { data } = useQuery(orpc.settings.warehouses.getKeyInfo.queryOptions({ input: { connectionId } }));
  if (!data) return null;
  // `recorded: false`: the key predates 3c's key record, so its dates are unknown (not "never").
  const created = data.recorded && data.createdAt ? `created ${date(data.createdAt)}` : 'created: not recorded';
  const rotated = !data.recorded
    ? 'rotated: not recorded'
    : data.rotatedAt
      ? `rotated ${date(data.rotatedAt)}`
      : 'never rotated';
  return (
    // `wrap-break-word whitespace-normal`, like the identity line right above it: the card is
    // `overflow-hidden`, and user · fingerprint · created · rotated is long enough to run off the
    // end of a narrow card and be clipped rather than wrapped.
    <span className="font-mono text-xs wrap-break-word whitespace-normal text-muted-foreground/80">
      {data.snowflakeUser ? `${data.snowflakeUser} · ` : ''}
      {data.source === 'workspace' ? 'Workspace key ' : 'Key '}
      {data.fingerprint ?? 'unreadable'} · {created} · {rotated}
    </span>
  );
}

/**
 * LC8: the SQL that removes the retired public key. The server sends it only to a user RudderStack
 * allows (ACT2-1276), so anyone else never sees this. Shown in the connection's View drawer, and on the rotate success screen —
 * never on the list (ACT2-1031).
 */
export function RetiredKeyAction({ connectionId }: { connectionId: string }): React.JSX.Element | null {
  const orpc = useOrpc();
  const syntaxStyle = useSyntaxStyle();
  const { data, refetch } = useQuery(orpc.settings.warehouses.getKeyInfo.queryOptions({ input: { connectionId } }));
  const withheld = data?.teardownWithheld;
  if (withheld === 'workspace_not_linked') {
    return (
      <Callout variant="warning" className="mt-4">
        Link a RudderStack workspace to see the SQL that removes the old key.
      </Callout>
    );
  }
  if (withheld === 'permission_unknown' || withheld === 'rudderstack_link_changed') {
    return (
      <Callout variant="warning" className="mt-4">
        <p>
          {withheld === 'permission_unknown'
            ? 'We could not check your RudderStack permission, so the SQL that removes the old key is not shown.'
            : 'The workspace was linked to another RudderStack workspace, so the SQL that removes the old key is not shown.'}
        </p>
        <div className="mt-2">
          <Button type="button" variant="secondary" label="Try again" onClick={() => void refetch()} />
        </div>
      </Callout>
    );
  }
  if (withheld === 'reauth_required') {
    return (
      <Callout variant="warning" className="mt-4">
        <p>Your RudderStack sign-in expired. Sign in again to see the SQL that removes the old key.</p>
        <div className="mt-2">
          <RudderstackSignInButton />
        </div>
      </Callout>
    );
  }
  if (!data?.retiredKeyTeardownSql) return null;
  return (
    <OutputSection
      className="mt-4"
      label="Remove the old key (optional, recommended)"
      action={<CopyButton text={data.retiredKeyTeardownSql} label="Copy" copyLabel="Teardown SQL" />}
    >
      <SyntaxHighlighter language="sql" style={syntaxStyle} customStyle={codeBlockCustomStyle}>
        {data.retiredKeyTeardownSql}
      </SyntaxHighlighter>
    </OutputSection>
  );
}
